import { gql, useQuery } from "@apollo/client";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import queryString from "query-string";
import React from "react";
import { useLocation } from "react-router-dom";
import { useBranchContext } from "../../../contexts/branchContext";
import useQueryString from "../../../hooks/useQueryString";
import AdminListContainer from "../../../styles/components/AdminListContainer";
import DataTable from "../../common/DataTable";
import Loading from "../../common/Loading";
import Meta from "../../common/Meta";
import Pagination from "../../common/Pagination";
import Search from "../../common/Search";

export const PRODUCT_CATEGORY_TYPES = gql`
  query ProductCategoryType {
    __type(name: "ProductCategoryType") {
      enumValues {
        name
      }
    }
  }
`;

export const PRODUCT_CATEGORIES = gql`
  query ProductCategories(
    $skip: Int
    $take: Int
    $where: ProductCategoryWhereInput
  ) {
    productCategories(skip: $skip, take: $take, where: $where) {
      id
      productCategoryType
      name
      description
      branch {
        id
        name
      }
    }
  }
`;

const PRODUCT_CATEGORIES_COUNT = gql`
  query ProductCategoriesCount($where: ProductCategoryWhereInput) {
    productCategoriesCount(where: $where)
  }
`;

const ProductCategoriesPage = () => {
  const title = `Product Categories`;
  const { branchFilterQueryString } = useBranchContext();
  const paramsObj = queryString.parse(useLocation().search);
  const page = parseInt(paramsObj.page) || 1;
  const perPage = 50;

  const {
    handleChangeQueryString: handleChangeSearch,
    handleSubmitQueryString: handleSubmitSearch,
    queryStringValue: searchTerm,
    initialQueryStringValue: searchQueryString,
  } = useQueryString("search");
  const {
    handleSubmitQueryString: handleSubmitFilter,
    initialQueryStringValue: filterQueryString,
  } = useQueryString("filter");

  const where = {
    ...(filterQueryString && {
      productCategoryType: {
        equals: filterQueryString,
      },
    }),
    ...(branchFilterQueryString && {
      branch: {
        id: {
          equals: branchFilterQueryString,
        },
      },
    }),
    ...(searchQueryString && {
      OR: [
        {
          name: {
            contains: searchQueryString,
            mode: "insensitive",
          },
        },
        {
          description: {
            contains: searchQueryString,
            mode: "insensitive",
          },
        },
      ],
    }),
  };
  const { data: { productCategories } = {}, loading } = useQuery(
    PRODUCT_CATEGORIES,
    {
      fetchPolicy: "network-only",
      variables: {
        skip: page * perPage - perPage,
        take: perPage,
        where,
      },
    }
  );

  const {
    data: { __type: { enumValues: productCategoryTypes } = {} } = {},
    loading: productCategoryTypesLoading,
  } = useQuery(PRODUCT_CATEGORY_TYPES, {
    fetchPolicy: "network-only",
  });

  const columns = [
    {
      header: "Branch",
      accessor: "branch.name",
    },
    {
      header: "Product Category Type",
      accessor: "productCategoryType",
      Cell: (children) => children && children.replace(/_/g, " "),
    },
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "Description",
      accessor: "description",
    },
  ];
  if (loading || productCategoryTypesLoading) return <Loading />;
  return (
    <>
      <Meta title={`${title} | Meatsumo`} />
      <AdminListContainer>
        <Typography variant="h3" color="primary" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          alignItems={{ xs: null, md: "flex-end" }}
          spacing={1}
        >
          <FormControl variant="outlined" size="small">
            <InputLabel id="productCategoryType-label">
              Product Category Type
            </InputLabel>
            <Select
              labelId="productCategoryType-label"
              label="Product Category Type"
              id="productCategoryType"
              name="productCategoryType"
              value={filterQueryString ?? ""}
              onChange={(e) => {
                handleSubmitFilter("direct")(e);
              }}
              sx={{
                backgroundColor: (theme) => theme.palette.common.white,
                minWidth: "300px",
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {productCategoryTypes?.map(({ name }) => (
                <MenuItem key={name} value={name}>
                  {name.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Search
            handleChangeSearch={handleChangeSearch}
            handleSubmitSearch={handleSubmitSearch()}
            searchTerm={searchTerm}
          />
        </Stack>

        <Paper className="dataTableContainer">
          <DataTable data={productCategories} columns={columns} />
        </Paper>
        <Pagination
          query={PRODUCT_CATEGORIES_COUNT}
          variables={{
            where,
          }}
          perPage={perPage}
          page={Number(page)}
        />
      </AdminListContainer>
    </>
  );
};

export default ProductCategoriesPage;
